import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import fileDownload from 'js-file-download';

import {getMessage as msg} from 'MessageBundle';
import conf from 'configuration.json';
import {validate, required, isNotContainedIn, forEach, generateStrategyResult, focusFirstError, findTableElement} from 'validation'
import BaseComponent from 'common/BaseComponent';
import Select from 'common/Select';
import Tooltip from 'common/Tooltip';
import Table from 'common/Table';
import Input from 'common/Input';
import ErrorBox from 'common/ErrorBox';
import theme from '../Admin.scss';
import ProgressBar from 'common/ProgressBar';
import {prepareFileName} from 'utils/FileName';

import addIcon from './newItem.png';
import readIcon from './readItem.png';
import editIcon from './editItem.png';
import deleteIcon from './deleteItem.png';
import notAllowedIcon from './actionNotAllowed.png';
import EmailLayoutPopup from './EmailLayoutPopup';

const WORD_TYPE = 'WORD';
const EMAIL_TYPE = 'EMAIL';
export default class LayoutTab extends BaseComponent {

    constructor(props) {
        super(props);

        this.state = {
            newName: "",
            newDescription: "",
            newType: "",
            layouts: [],
            defaultSender: "",
            fileToken: undefined,
            processing: false,
            activeEmailLayout: undefined
        };
        this.layoutTypes = new Map([
            [EMAIL_TYPE, msg('admin.layoutTab.emailType')],
            [WORD_TYPE, msg('admin.layoutTab.wordType')]
        ]);
        this.tableStructure = [
            {
                attribute: "name", label: <span>{msg("admin.layoutTab.columnName")} <Tooltip value={msg('admin.layoutTab.columnNameTooltip')}/></span>,
                width: '250px',
                render: (row, index) => {
                    return <Input id={"displayableName" + index} name="displayableName" size={30} value={row.displayableName} onChange={this.handleLayoutChange('displayableName', index)}
                        errorMessages={this.state.errorMessages} index={index} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "description", label: msg("admin.layoutTab.columnDescription"), width: '390px',
                render: (row, index) => {
                    return <Input id={"description" + index} name="description" size={30} value={row.description} onChange={this.handleLayoutChange('description', index)}
                        errorMessages={this.state.errorMessages} index={index} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "type", label: msg("admin.layoutTab.columnType"), width: '130px',
                render: (row, index) => {
                    return (
                        <Select id={"layout-select" + index} options={this.layoutTypes} value={row.type} disabled/>
                    );
                }
            },
            {
                attribute: "action", label: msg("admin.layoutTab.columnAction"), width: '145px', headStyle: theme.columnAction, bodyStyle: theme.columnAction,
                render: (row, index) => {
                    return (
                        <div>
                            <a onClick={this.handleRead(index)} href="javascript:void(0)" className={theme.readIcoLnk}
                                title={msg("admin.layoutTab.viewItem") + ' ' + prepareFileName(row.name)}>
                                <img src={readIcon} alt=""/>
                            </a>
                            {row.deletable ?
                                <a onClick={this.handleDelete(index)} href="javascript:void(0)" className={theme.deleteIcoLnk}
                                    title={msg("admin.layoutTab.deleteItem")}>
                                    <img src={deleteIcon} alt=""/>
                                </a> :
                                <img src={notAllowedIcon} title={msg("admin.layoutTab.deleteNotAllowedItem")}
                                    alt={msg("admin.layoutTab.deleteNotAllowedItem")} className={theme.notAllowedIcoLnk}/>
                            }
                            <a onClick={this.handleEdit(index)} href="javascript:void(0)" className={theme.editIcoLnk}
                                title={msg("admin.layoutTab.editItem") + ' ' + prepareFileName(row.name)}>
                                <img src={editIcon} alt=""/>
                            </a>
                        </div>
                    );
                }
            }
        ];

        this.lastRowStructure = [
            {
                attribute: "name", label: msg("admin.layoutTab.columnName"), width: '250px',
                render: () => {
                    return <Input id="newName-txt" name="newName" value={this.state.newName} onChange={this.handleChange('newName')}
                        errorMessages={this.state.errorMessages} showExclamationMark={false}/>
                }
            },
            {
                attribute: "description", label: msg("admin.layoutTab.columnDescription"), width: '390px',
                render: () => {
                    return <Input id="newDescription-txt" name="newDescription" value={this.state.newDescription} onChange={this.handleChange('newDescription')}
                        errorMessages={this.state.errorMessages} showExclamationMark={false}/>
                }
            },
            {
                attribute: "type", label: msg("admin.layoutTab.columnType"), width: '130px',
                render: () => {
                    return (
                        <Select id="newType-select" name="newType" options={this.layoutTypes} value={this.state.newType} onChange={this.handleChange('newType')}
                            errorMessages={this.state.errorMessages} showExclamationMark={false}/>
                    );
                }
            },
            {
                attribute: "action", label: msg("admin.layoutTab.columnAction"), width: '145px', headStyle: theme.columnAction, bodyStyle: theme.columnAction,
                render: () => {
                    return (
                        <a href="javascript:void(0)" onClick={this.handleCreate} title={msg("admin.layoutTab.addItem")}>
                            <img src={addIcon} alt=""/>
                        </a>
                    );
                }
            }
        ];

        const isNotDuplicated = ([displayableName]) => {
            const errors = [];
            const count = this.state.layouts.map(t => t.displayableName).filter(name => name === displayableName).length;
            if (count > 1) {
                errors.push(msg('admin.layoutTab.validation.duplicateValue'));
            }
            return generateStrategyResult(errors)
        };

        this.validation = {
            layoutTab: [
                {
                    properties: ["state.newName"],
                    individualNames: ["newName"],
                    strategies: [
                        required(msg('admin.layoutTab.validation.nameEmpty'))
                    ]
                }, {
                    properties: ["state.newName", "state.layouts"],
                    individualNames: ["newName"],
                    strategies: [
                        isNotContainedIn("displayableName", msg('admin.layoutTab.validation.duplicateValue'))
                    ]
                }, {
                    properties: ["state.newDescription"],
                    individualNames: ["newDescription"],
                    strategies: [
                        required(msg('admin.layoutTab.validation.descriptionEmpty'))
                    ]
                }, {
                    properties: ["state.newType"],
                    individualNames: ["newType"],
                    strategies: [
                        required(msg('admin.layoutTab.validation.typeEmpty'))
                    ]
                }
            ],
            save: [
                {
                    properties: ["state.layouts"],
                    strategies: [
                        forEach([
                            {
                                properties: ["displayableName"],
                                individualNames: ["displayableName"],
                                strategies: [
                                    required(msg('admin.layoutTab.validation.nameEmpty')),
                                    isNotDuplicated
                                ]
                            }, {
                                properties: ["description"],
                                individualNames: ["description"],
                                strategies: [
                                    required(msg('admin.layoutTab.validation.descriptionEmpty'))
                                ]
                            }
                        ])
                    ]
                }
            ]
        };

        this.elementOrder = {
            newName : "newName-txt",
            newDescription: "newDescription-txt",
            newType: "newType-select",
            displayableName: findTableElement("displayableName"),
            description: findTableElement("description")
        };
    }

    reloadData = () => {
        this.setState({processing: true});

        this.request('get', conf.rest.documentLayoutEndpoint).send((response) => {
            this.setState({
                layouts: response.body.layouts,
                defaultSender: response.body.defaultSender,
                fileToken: response.body.fileToken,
                newName: "",
                newDescription: "",
                newType: "",
                processing: false,
                dataChanged: false,
                errorMessages: []
            })
        });
    };

    handleLayoutChange = (field, index) => (e) => {
        this.setState({
            layouts: update(this.state.layouts, {[index]: {[field]: {$set: e.target.value}}}),
            dataChanged: true
        })
    };

    handleReload = () => {
        if (!this.state.dataChanged || confirm(msg("admin.confirmResetChanges"))){
            this.reloadData();
        }
    };

    handleCreate = () => {
        const validationResult = validate(this, this.validation, ["layoutTab"]);
        if (validationResult.isEmpty()) {
            const createNewLayout = (layoutName, sender, onCreate) => {
                const layouts = update(this.state.layouts, {$push: [{
                    name: layoutName,
                    displayableName: this.state.newName,
                    description: this.state.newDescription,
                    type: this.state.newType,
                    sender: sender,
                    deletable: true
                }]});
                this.setState({
                    layouts: layouts,
                    newName: "",
                    newType: "",
                    newDescription: "",
                    dataChanged: true,
                    errorMessages: []
                }, onCreate(this.state.layouts.length));
            };
            if (this.state.newType === WORD_TYPE) {
                const newLayoutName = this.state.newName + '.docx';
                this.request('post', conf.rest.wordLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(newLayoutName)).send(() => {
                    createNewLayout(newLayoutName, undefined, this.handleEdit);
                });
            } else {
                const newLayoutName = this.state.newName + '.html';
                this.request('post', conf.rest.emailLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(newLayoutName)).send(() => {
                    createNewLayout(newLayoutName, this.state.defaultSender, this.handleEdit);
                });
            }
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    handleRead = (index) => () => {
        const layout = this.state.layouts[index];
        if (layout.type === WORD_TYPE) {
            this.request('get', conf.rest.wordLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(layout.name)).send((response) => {
                const byteArray = new Uint8Array(this.strToByteArray(response.body.file));
                fileDownload(new Blob([byteArray]), layout.displayableName + '.docx');
            });
        } else {
            this.showEmailLayout(layout.name, layout.sender, true);
        }
    };

    handleEdit = (index) => () => {
        const layout = this.state.layouts[index];
        if (layout.type === WORD_TYPE) {
            this.request('put', conf.rest.wordLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(layout.name)).send((response) => {
                window.location.href = response.body.url;
            });
            this.setState({
                dataChanged: true
            });
        } else {
            this.showEmailLayout(layout.name, layout.sender, false);
        }
    };

    showEmailLayout = (name, sender, readonly) => {
        this.request('get', conf.rest.emailLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(name)).send((response) => {
            this.setState({
                activeEmailLayout: {
                    name: name,
                    sender: sender,
                    content: response.body.content,
                    readonly: readonly
                },
                activeFocus: document.activeElement
            });
        });
    };

    handleEmailLayoutClose = () => {
        this.state.activeFocus.focus();
        this.setState({
            activeEmailLayout: undefined,
            activeFocus: undefined
        });
    };

    handleEmailLayoutSave = (sender, content) => {
        this.setState({processing: true});
        const {name} = this.state.activeEmailLayout;

        this.request('put', conf.rest.emailLayoutEndpoint + '/' + this.state.fileToken + '/' + encodeURIComponent(name)).params({content: content}).send(() => {
            this.state.activeFocus.focus();
            const layoutIndex = this.state.layouts.findIndex(l => l.name === name);
            this.setState({
                layouts: update(this.state.layouts, {[layoutIndex]: {sender: {$set: sender}}}),
                activeEmailLayout: undefined,
                activeFocus: undefined,
                processing: false,
                dataChanged: true
            });
        });
    };

    handleDelete = (index) => () => {
        if (confirm(msg('admin.layoutTab.deleteConfirmation'))) {
            this.setState({
                layouts: update(this.state.layouts, {$splice: [[index, 1]]}),
                dataChanged: true
            });
        }
    };

    handleSave = () => {
        const validationResult = validate(this, this.validation, ["save"]);
        if (validationResult.isEmpty()) {
            this.setState({processing: true});
            const params = {
                layouts: this.state.layouts,
                fileToken: this.state.fileToken
            };
            this.request('post', conf.rest.documentLayoutEndpoint).params(params).send((response) => {
                this.setState({
                    layouts: response.body.layouts,
                    errorMessages: [],
                    processing: false,
                    dataChanged: false
                })
            });
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    render() {
        return (
            <div className={theme.layoutTab}>
                <ErrorBox groups={["layoutTab", "save"]} errorMessages={this.state.errorMessages}/>
                <div className={theme.tblWrap}>
                    <div className={theme.tblWrapInner}>
                        <Table structure={this.tableStructure} lastRowStructure={this.lastRowStructure}
                            value={this.state.layouts} className={theme.layoutTabTbl}/>
                    </div>
                </div>

                <div className={theme.btnGroupMini + " " + theme.btnAdmin}>
                    <span>{msg("admin.layoutTab.changeBtnsInfo")}</span>
                    <button onClick={this.handleReload}>
                        {msg("admin.layoutTab.skipChangesBtn")}
                    </button>
                    <button onClick={this.handleSave} disabled={!this.state.dataChanged} className={theme.buttonAccent}>
                        {msg("admin.layoutTab.saveChangesBtn")}
                    </button>
                    <span className={theme.btnDecor}></span>
                    <button onClick={this.props.onFieldListClick}>
                        {msg("admin.layoutTab.openLayoutFieldsBtn")}
                    </button>
                </div>
                <ProgressBar hidden={!this.state.processing} />
                {this.state.activeEmailLayout ?
                    <EmailLayoutPopup visible={true} layout={this.state.activeEmailLayout}
                        onClose={this.handleEmailLayoutClose} onSave={this.handleEmailLayoutSave}/>
                    : null
                }
            </div>
        )
    }
}

LayoutTab.propTypes = {

    /**
     * JavaScript that has to be executed on Field List Button click.
     */
    onFieldListClick: PropTypes.func.isRequired
};


// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/Admin/LayoutTab/index.js